import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Eye,
  Download,
  Loader2,
  RefreshCw,
  Search,
  AlertCircle,
  XCircle,
  FileIcon,
  Calendar,
  HardDrive,
  CloudUpload,
  ArrowLeft,
  Edit,
  Delete,
} from "lucide-react";
import { useFile } from "../context/filecontext";
import { useNavigate } from "react-router-dom";
import Header from "../components/header";

// Create axios instance with base URL
const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 300000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

console.log("The Base url is ", axiosInstance.defaults.baseURL);
const FileManager = () => {
  const {
    setMetadataUrl,
    clearMetadataUrl,
    showNotificationWithConfig,
    setUploadResponseData,
  } = useFile();

  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingFile, setProcessingFile] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const navigate = useNavigate();
  const CACHE_KEY = "file_manager_cache";
  const CACHE_TIME = 5 * 60 * 1000; // 5 minutes

  const handleApiError = (error) => {
    console.error("API Error:", error);
    const errorMessage =
      error.response?.data?.detail ||
      error.message ||
      "An unexpected error occurred";
    return String(errorMessage);
  };

  const parseApiResponse = (response) => {
    try {
      const data = response.data;

      if (Array.isArray(data)) {
        return data;
      }

      if (data?.files && Array.isArray(data.files)) {
        return data.files;
      }
      if (data?.items && Array.isArray(data.items)) {
        return data.items;
      }

      if (typeof data === "string") {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }

      console.error("Invalid data structure:", data);
      throw new Error("Could not find valid file list in response");
    } catch (error) {
      console.error("Parse error details:", error);
      throw new Error(`Failed to parse server response: ${error.message}`);
    }
  };

  const cacheFiles = (data) => {
    const cacheData = {
      timestamp: new Date().getTime(),
      data,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  };

  const fetchFiles = async (fromDate) => {
    setIsLoading(true);
    setError(null);

    try {
      //const response = await axiosInstance.get("/s3/files");
      const response = await axiosInstance.get(
        "/retrivescanrecord/2025-02-18/2025-02-28"
      );

      let files = parseApiResponse(response);

      if (!Array.isArray(files)) {
        throw new Error("Data is not an array after normalization");
      }

      /* const lasFiles = files
        .filter((file) => {
          const isValid =
            file &&
            typeof file === "object" &&
            file.file_name &&
            typeof file.file_name === "string" &&
            file.file_name.toLowerCase().endsWith(".las");
          return isValid;
        })
        .map((file) => ({
          ...file,
          displayName: file.file_name.split("/").pop(),
          size: file.size
            ? `${(file.size / 1024).toFixed(2)} KB`
            : "Unknown size",
          lastModified: file.last_modified
            ? new Date(file.last_modified).toLocaleString()
            : "Unknown date",
          downloadUrl: file.download_url || "",
        })); */

      const lasFiles = files.map((file) => ({
        ...file,
        scanId: file.scanId,
        scanCode: file.ScanCode,
        displayName: file.ScanName,
        actualvolume: "12575",
        plannedvolume: "11543",
        downloadUrl: file.ScanId || "",
      }));

      setFiles(lasFiles);
      setFilteredFiles(lasFiles);
      cacheFiles(lasFiles);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(`Error loading files: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = async (fileName, index) => {
    if (processingFile) {
      showNotificationWithConfig({
        type: "warning",
        message: "Please wait for the current file to finish processing",
        duration: 3000,
      });
      return;
    }

    setProcessingFile(fileName);
    setSelectedFileId(index);
    setError(null);

    try {
      clearMetadataUrl();
      setShowTimeline(true);

      const response = await axiosInstance.post("/s3/view/", {
        file_path: fileName,
      });

      if (response.data && response.data.file_id) {
        const baseUrl = BASE_URL;
        const potreePath = `${baseUrl}/files/${response.data.file_id}/metadata.json`;

        setMetadataUrl(potreePath);
        setUploadResponseData({
          file_id: response.data.file_id,
          metadata_url: potreePath,
          octree_url: `${baseUrl}/files/${response.data.file_id}/octree.bin`,
          las_url : "https://private-bucket-mine.s3.ap-south-1.amazonaws.com/Files/c3VzaGls/flat-design/preprocessfiles/556_N_BH4_B4.las",

        });

        showNotificationWithConfig({
          type: "success",
          message: "File loaded successfully",
          duration: 3000,
        });

        navigate("/viewer", {
          state: { fallbackUrl: "/file-manager" },
        });
      } else {
        throw new Error("Failed to get viewer URLs");
      }
    } catch (err) {
      console.error("View error:", err);
      const errorMessage = handleApiError(err);
      setError(errorMessage);

      showNotificationWithConfig({
        type: "error",
        message: `Error loading file: ${errorMessage}`,
        duration: 5000,
      });

      clearMetadataUrl();
    } finally {
      setProcessingFile(null);
      setSelectedFileId(null);
    }
  };

  const handleCancelProcessing = () => {
    if (processingFile) {
      setProcessingFile(null);
      setSelectedFileId(null);
      clearMetadataUrl();

      showNotificationWithConfig({
        type: "info",
        message: "File processing cancelled",
        duration: 3000,
      });
    }
  };

  const handleDownload = (file) => {
    if (!file.downloadUrl) {
      showNotificationWithConfig({
        type: "error",
        message: "Download URL not available",
        duration: 3000,
      });
      return;
    }

    setDownloadingFile(file.file_name);

    try {
      const link = document.createElement("a");
      link.href = file.downloadUrl;
      link.download = file.displayName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showNotificationWithConfig({
        type: "success",
        message: "Download started successfully",
        duration: 3000,
      });
    } catch (err) {
      console.error("Download error:", err);
      setError("Failed to initiate download");
      showNotificationWithConfig({
        type: "error",
        message: "Failed to download file",
        duration: 3000,
      });
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleSearch = async () => {
    try {
      if (!fromDate || !toDate) {
        throw new Error("Please select both From Date and To Date");
      }

      console.log("From Date:", fromDate, "To Date:", toDate);

      const response = await fetch(
        `https://9qmbrb3t5c.us-east-1.awsapprunner.com/retrivescanrecord/${encodeURIComponent(
          fromDate
        )}/${encodeURIComponent(toDate)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const files = await response.json();
      if (!Array.isArray(files)) {
        throw new Error("Data is not an array after normalization");
      }

      const lasFiles = files.map((file) => ({
        ...file,
        scanId: file.scanId,
        scanCode: file.ScanCode,
        displayName: file.ScanName,
        actualvolume: "12575",
        plannedvolume: "11543",
        downloadUrl: file.ScanId || "",
      }));

      setFiles(lasFiles);
      setFilteredFiles(lasFiles);
      cacheFiles(lasFiles);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(`Error loading files: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const filtered = files.filter((file) =>
      file.displayName.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredFiles(filtered);
  }, [searchQuery, files]);

  useEffect(() => {
    fetchFiles();
  }, []);

  const renderFileCard = (file, index) => (
    <div className="overflow-x-auto p-6 bg-gray-100 rounded-lg shadow-md">
      <table className="w-full border-collapse bg-white rounded-xl shadow-lg border text-gray-900">
        <thead className="sticky top-0 bg-gray-800 text-white text-left text-sm font-semibold">
          <tr>
            <th className="p-4 rounded-tl-xl">File Id</th>
            <th className="p-4">File Code</th>
            <th className="p-4">File Name</th>
            <th className="p-4">Actual Volume</th>
            <th className="p-4">Planned Volume</th>
            <th className="p-4 text-center rounded-tr-xl">Actions</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file, index) => (
            <tr
              key={index}
              className={`border-b transition-all duration-300 hover:shadow-md hover:bg-gray-50 odd:bg-gray-50 even:bg-white 
              ${
                selectedFileId === index
                  ? "ring-2 ring-blue-500 bg-blue-50"
                  : ""
              }`}
            >
              <td className="p-4 font-semibold text-gray-900 flex items-center space-x-2">
                <FileIcon className="h-5 w-5 text-blue-500" />
                <span className="truncate max-w-xs">{file.ScanId}</span>
              </td>
              <td className="p-4 text-sm text-gray-600">{file.scanCode}</td>
              <td className="p-4 text-sm text-gray-600">{file.displayName}</td>
              <td className="p-4 text-sm text-gray-600">{file.actualvolume}</td>
              <td className="p-4 text-sm text-gray-600">
                {file.plannedvolume}
              </td>
              <td className="p-4 flex justify-center space-x-3">
                <button
                  onClick={() => handleDownload(file.ScanId)}
                  disabled={downloadingFile === file.ScanId}
                  className="px-3 py-2 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-100 transition-all duration-200 shadow-sm hover:shadow disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                  title="Download File"
                >
                  {downloadingFile === file.ScanId ? (
                    <Loader2 className="h-5 w-5 mr-1 animate-spin" />
                  ) : (
                    <Download className="h-5 w-5 mr-1" />
                  )}
                  Download
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Fixed Header Section */}
      <header>
        <Header />
      </header>
      <div className="flex-none bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between p-4 bg-white shadow-sm rounded-md">
            <h2 className="text-xl font-semibold text-gray-900">
              Download Report
            </h2>

            <div className="flex gap-3">
              <button
                onClick={() => navigate("/")}
                className="flex items-center px-4 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Home
              </button>
              <button
                onClick={fetchFiles}
                className="flex items-center px-4 py-1.5 text-sm text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-1 ${isLoading ? "animate-spin" : ""}`}
                />
                {isLoading ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>

          <div className="p-6 bg-white shadow-md rounded-xl border border-gray-200">
            {/* Date Range Filter */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 items-end">
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700">
                  From Date
                </label>
                <input
                  type="date"
                  className="mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm text-gray-700"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700">
                  To Date
                </label>
                <input
                  type="date"
                  className="mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm text-gray-700"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <button
                  className="w-full px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold flex items-center justify-center space-x-2 hover:bg-blue-700 transition-all shadow-md"
                  onClick={handleSearch}
                >
                  <Search className="h-5 w-5 text-white" />
                  <span>Search</span>
                </button>
              </div>
            </div>

            {/* Search Bar */}
            {/* <div className="mt-6 relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search files by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm text-gray-700"
              />
            </div> */}
          </div>
        </div>
      </div>

      {/* Scrollable Content Section */}

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {error && (
            <div className="mb-6">
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <p className="text-sm text-gray-500">Loading files...</p>
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-4 text-gray-500">
              <FileIcon className="h-12 w-12 stroke-1" />
              <p className="text-lg">No LAS files available</p>
              {searchQuery && (
                <p className="text-sm text-gray-400">
                  No files match your search criteria
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFiles.map((file, index) => renderFileCard(file, index))}
            </div>
          )}
        </div>
      </div>

      {showTimeline && processingFile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Processing File
              </h3>
              <button
                onClick={handleCancelProcessing}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
              <p className="ml-3 text-gray-600">Processing your file...</p>
            </div>
          </div>
        </div>
      )}

      {/* Error notification */}
      {error && (
        <div className="fixed bottom-4 right-4 max-w-md bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => setError(null)}
                  className="inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileManager;
